import {
  UsersGetNotesQueryRouteResponse,
  UsersGetProfileImageUrlRouteResponse,
  UsersGetStatusRouteResponse,
  UsersPostPasswordResetRouteResponse,
  UsersPutDisableRouteResponse,
  UsersPutEnableRouteResponse,
  UsersPutMfaUpdateRouteResponse,
  UsersPutUpdateRouteBodyInput,
  UsersPutUpdateRouteResponse,
} from '@repo/types/api';

import useUserGroups from '../../utils/react/hooks/useUserGroups';
import useUserId from '../../utils/react/hooks/useUserId';
import { cognitoApi } from '../api';
import { useGetStaffQuery } from '../staff';
import { useGetStudentQuery } from '../students';
import { attachIds, EntityResponse } from '../utils';

import type { UserNote } from './types';

import { UserGroup } from '@repo/validation';

// Define a service using a base URL and expected endpoints
export const usersApi = cognitoApi.injectEndpoints({
  endpoints: build => ({
    updateUser: build.mutation<
      UsersPutUpdateRouteResponse,
      { userId: string; newAttributes: UsersPutUpdateRouteBodyInput }
    >({
      query(arg) {
        const { userId, newAttributes } = arg;
        return {
          url: `users/${userId}/update`,
          body: newAttributes,
          method: 'PUT',
        };
      },
      // Invalidates all queries that subscribe to this User
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Users', id: arg.userId },
        { type: 'Students', id: arg.userId },
        { type: 'Staff', id: arg.userId },
      ],
    }),
    disableUser: build.mutation<UsersPutDisableRouteResponse, { userId: string }>({
      query(arg) {
        const { userId } = arg;
        return {
          url: `users/${userId}/disable`,
          method: 'PUT',
        };
      },
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Users', id: arg?.userId },
        { type: 'Students', id: arg?.userId },
        { type: 'Staff', id: arg?.userId },
      ],
    }),
    enableUser: build.mutation<UsersPutEnableRouteResponse, { userId: string }>({
      query(arg) {
        const { userId } = arg;
        return {
          url: `users/${userId}/enable`,
          method: 'PUT',
        };
      },
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Users', id: arg?.userId },
        { type: 'Students', id: arg?.userId },
        { type: 'Staff', id: arg?.userId },
      ],
    }),
    getProfileImageUrl: build.query<UsersGetProfileImageUrlRouteResponse, { userId: string }>({
      query: arg => `users/${arg.userId}/profileImageUrl`,
      // No tags since this is a temporary upload URL that doesn't need caching
    }),
    getUserNotes: build.query<
      EntityResponse<UserNote[]>,
      {
        active?: boolean;
        dueBeforeDatetime?: number;
        limit?: number | 'all';
      }
    >({
      query(arg) {
        const { active, dueBeforeDatetime, limit } = arg;

        return {
          url: 'users/notes/query',
          params: { active, dueBeforeDatetime, limit },
          method: 'GET',
        };
      },
      providesTags: result =>
        result
          ? [...result.data.map(({ id }) => ({ type: 'UserNotes' as const, id })), 'UserNotes']
          : ['UserNotes'],
      transformResponse(response: UsersGetNotesQueryRouteResponse) {
        return attachIds(response, entry => `${entry.userId}#${entry.creationDatetime}`);
      },
    }),
    getUserStatus: build.query<UsersGetStatusRouteResponse, { userId: string }>({
      query(arg) {
        return {
          url: `users/${arg.userId}/status`,
          method: 'GET',
        };
      },
      providesTags: (_result, _error, arg) => [
        { type: 'Users', id: arg.userId },
        { type: 'Students', id: arg.userId },
        { type: 'Staff', id: arg.userId },
      ],
    }),
    resetTemporaryPassword: build.mutation<UsersPostPasswordResetRouteResponse, { userId: string }>(
      {
        query(arg) {
          const { userId } = arg;
          return {
            url: `users/${userId}/password/reset`,
            method: 'POST',
          };
        },
        invalidatesTags: (_result, _error, arg) => [{ type: 'Users', id: arg.userId }],
      }
    ),
    updateUserMfaMethod: build.mutation<
      UsersPutMfaUpdateRouteResponse,
      {
        userId: string;
        mfaMethod: string;
        isTotpSetup?: boolean;
      }
    >({
      query(arg) {
        const { userId, mfaMethod, isTotpSetup } = arg;
        return {
          url: `users/${userId}/mfa/update`,
          body: { mfaMethod, isTotpSetup },
          method: 'PUT',
        };
      },
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Users', id: arg.userId },
        { type: 'Students', id: arg.userId },
        { type: 'Staff', id: arg.userId },
      ],
    }),
  }),
});

export const {
  useUpdateUserMutation,
  useDisableUserMutation,
  useEnableUserMutation,
  useLazyGetProfileImageUrlQuery,
  useResetTemporaryPasswordMutation,
  useGetUserStatusQuery,
  useGetUserNotesQuery,
  useUpdateUserMfaMethodMutation,
} = usersApi;

interface CurrentUserQueryOptions {
  skip?: boolean;
}

export {
  getCurrentUserEmail,
  getCurrentUserPhoneNumber,
  getProfileIconParts,
  type CurrentUserData,
} from './utils';

// Fetch the current user from the students or staff endpoint, depending on
// the caller's groups. Both hooks are always called (one skipped) so the
// hook order stays stable when the groups resolve asynchronously.
export const useGetCurrentUserQuery = (options?: CurrentUserQueryOptions) => {
  const groups = useUserGroups() || [];
  const userId = useUserId() || '';

  const isStudent = groups.includes(UserGroup.Students);
  const skip = Boolean(options?.skip) || groups.length === 0 || userId.length === 0;

  const studentResult = useGetStudentQuery({ studentId: userId }, { skip: skip || !isStudent });
  const staffResult = useGetStaffQuery({ staffId: userId }, { skip: skip || isStudent });

  return isStudent ? studentResult : staffResult;
};
